import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { LiveNow } from '../types/types';
import { isLiveFresh, LIVE_FRESH_MS, normalizeLiveNow } from '../utils/live';

/* What the Knesset is broadcasting now: meta/liveNow (~2 KB), rewritten every minute by the
   syncLive job. Same pattern as hooks/usePresence: every component that shows it shares one
   copy, read when the first one mounts, then again every `refreshMs` (default a minute)
   while a page that shows it is open and in view, and right away when the tab comes back
   into view. Merge it with the schedule through utils/live mergeLive.

   Development builds only: ?liveDemo=1 shows a sample live sitting and committees
   (dev/liveDemo), also in meta/scheduleNow; ?liveDemo=0 turns it off. */

const DEFAULT_REFRESH_MS = 60 * 1000;
// A read younger than this is reused (under the shortest refresh: the timer starts before
// the first read resolves, so an equal window would swallow every other refresh)
const REUSE_MS = 30 * 1000;

export const liveDemoOn = (): boolean => {
  if (process.env.NODE_ENV !== 'development') return false;
  try {
    const q = window.location.search;
    if (/[?&]liveDemo=1/.test(q)) sessionStorage.setItem('liveDemo', '1');
    if (/[?&]liveDemo=0/.test(q)) sessionStorage.removeItem('liveDemo');
    return sessionStorage.getItem('liveDemo') === '1';
  } catch {
    return false;
  }
};

interface Snapshot { live: LiveNow | null; readAt: number }

let current: Snapshot | null = null;
let inflight: Promise<Snapshot> | null = null;
const listeners: Array<(s: Snapshot) => void> = [];

const loadNow = (): Promise<Snapshot> => {
  if (current && Date.now() - current.readAt < REUSE_MS) return Promise.resolve(current);
  if (!inflight) {
    const read: Promise<LiveNow | null> = process.env.NODE_ENV === 'development' && liveDemoOn()
      ? import('../dev/liveDemo').then(m => m.demoLiveNow())
      : getDoc(doc(db, 'meta', 'liveNow')).then(snap => (snap.exists() ? normalizeLiveNow(snap.data() as Partial<LiveNow>) : null));
    inflight = read.then(live => {
      current = { live, readAt: Date.now() };
      inflight = null;
      const snap = current;
      listeners.slice().forEach(l => l(snap));
      return snap;
    }, (err: Error) => {
      inflight = null;
      throw err;
    });
  }
  return inflight;
};

// After a failed read (an error state's retry button): read again now
export const retryLiveNow = (): void => {
  current = current ? { ...current, readAt: 0 } : null;
  loadNow().catch(() => { /* the hooks keep showing the error */ });
};

export interface LiveNowState {
  // undefined while loading, null when the doc doesn't exist (the poller isn't deployed)
  live: LiveNow | null | undefined;
  // The last read failed (`live` keeps the previous copy, if any)
  error: boolean;
  // Date.now(), updated every 30 s and on every read: pass it to mergeLive, so the time
  // rule and "since HH:MM" move on
  clock: number;
  // The feed can be trusted: it was fresh (polled within 3 minutes) when it was read, and
  // the read is recent (within refreshMs + 3 minutes). Pass it as mergeLive's opts.fresh.
  fresh: boolean;
}

// refreshMs: how often to re-read while shown (at least a minute; the desktop sidebar's badge
// can use e.g. 180 s)
export const useLiveNow = (options: { refreshMs?: number } = {}): LiveNowState => {
  const refreshMs = Math.max(options.refreshMs || DEFAULT_REFRESH_MS, REUSE_MS * 2);
  const [snap, setSnap] = useState<Snapshot | undefined>(current || undefined);
  const [error, setError] = useState(false);
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    const onData = (s: Snapshot) => {
      if (!alive) return;
      setSnap(s);
      setError(false);
      setClock(Date.now());
    };
    listeners.push(onData);
    const refresh = () => { loadNow().then(onData).catch(() => { if (alive) setError(true); }); };
    refresh();
    const poll = window.setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, refreshMs);
    const tick = window.setInterval(() => { if (alive) setClock(Date.now()); }, 30 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      const i = listeners.indexOf(onData);
      if (i >= 0) listeners.splice(i, 1);
      window.clearInterval(poll);
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshMs]);

  const live = snap ? snap.live : undefined;
  const fresh = !!snap && isLiveFresh(snap.live, snap.readAt) && clock - snap.readAt <= refreshMs + LIVE_FRESH_MS;
  return { live, error, clock, fresh };
};
